import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await authAPI.me();
        if (response.data.success) {
          setUser(response.data.data.user);
        } else {
          localStorage.removeItem('token');
        }
      } catch (error) {
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  };

  const login = async (credential, password) => {
    try {
      const response = await authAPI.login({ credential, password });
      if (response.data.success) {
        const { user, token } = response.data.data;
        localStorage.setItem('token', token);
        setUser(user);
        return { success: true };
      }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Login failed' };
    }
  };

  const register = async (data) => {
    try {
      const response = await authAPI.register(data);
      if (response.data.success) {
        const { user, token } = response.data.data;
        localStorage.setItem('token', token);
        setUser(user);
        return { success: true };
      }
    } catch (error) {
      // Handle validation errors
      if (error.response?.data?.errors && error.response.data.errors.length > 0) {
        const errorMessages = error.response.data.errors.map(err => err.msg || err.message).join(', ');
        return { success: false, message: errorMessages };
      }
      return { success: false, message: error.response?.data?.message || 'Registration failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  // Refresh user data from server
  const refreshUser = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await authAPI.me();
        if (response.data.success) {
          setUser(response.data.data.user);
          return response.data.data.user;
        }
      } catch (error) {
        console.error('Failed to refresh user:', error);
      }
    }
    return null;
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isVip: user?.role === 'vip' || user?.role === 'admin',
    loading,
    login,
    register,
    logout,
    checkAuth,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;

